import { z } from 'zod';
import type { EtapaPreparacao } from './estoque';

/** Tema do Site Premium (o padrão usa só cor e logo da loja). */
export const temaSiteSchema = z.object({
  corPrimaria: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  corSecundaria: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  fonte: z.enum(['sans', 'serif', 'condensada']).default('sans'),
  layout: z.enum(['classico', 'vitrine', 'compacto']).default('classico'),
  cantos: z.enum(['retos', 'suaves', 'redondos']).default('suaves'),
});
export type TemaSite = z.infer<typeof temaSiteSchema>;

export const simuladorConfigSchema = z.object({
  taxaMensalPercentual: z.number().min(0).max(10).default(1.99),
  prazos: z.array(z.number().int().min(6).max(84)).min(1).default([12, 24, 36, 48, 60]),
  entradaMinimaPercentual: z.number().min(0).max(90).default(20),
});
export type SimuladorConfig = z.infer<typeof simuladorConfigSchema>;
export const SIMULADOR_PADRAO: SimuladorConfig = simuladorConfigSchema.parse({});

export { calcularParcela, simular } from './simulador';

/** Slug amigável: marca-modelo-ano-cidade-<8 primeiros do id>. */
export function slugVeiculo(
  v: { id: string; marca: string; modelo: string; anoModelo: number },
  cidade?: string | null,
): string {
  const limpa = (s: string) =>
    s
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  return [
    limpa(v.marca),
    limpa(v.modelo),
    String(v.anoModelo),
    cidade ? limpa(cidade) : '',
    v.id.slice(0, 8),
  ]
    .filter(Boolean)
    .join('-');
}
export const idDoSlug = (slug: string): string | null => {
  const m = /([0-9a-f]{8})$/.exec(slug);
  return m ? m[1]! : null;
};

/** Anti-robô dos formulários públicos: honeypot vazio e tempo mínimo de preenchimento. */
const antiRobo = {
  _hp: z.string().max(0, 'robô').optional().default(''),
  _t: z.number().int().min(1500, 'Envio rápido demais'),
  captcha: z.string().max(4096).optional(),
};

const telefone = z
  .string()
  .transform((t) => t.replace(/\D/g, ''))
  .refine((t) => /^\d{10,13}$/.test(t), 'Telefone com DDD');

export const leadPublicoSchema = z.object({
  nome: z.string().trim().min(2, 'Informe seu nome').max(120),
  telefone,
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email()
    .max(254)
    .optional()
    .or(z.literal('').transform(() => undefined)),
  mensagem: z.string().trim().max(2000).optional(),
  veiculoId: z.string().uuid().optional(),
  origem: z.enum(['site', 'whatsapp', 'campanha']).default('site'),
  campanha: z.string().max(80).optional(),
  consentimento: z.literal(true, {
    error: 'É preciso concordar com o uso dos dados para contato',
  }),
  ...antiRobo,
});
export type LeadPublico = z.input<typeof leadPublicoSchema>;

export const simulacaoPublicaSchema = z.object({
  veiculoId: z.string().uuid(),
  entradaCentavos: z.number().int().min(0),
  prazo: z.number().int().min(6).max(84),
  intencao: z.enum(['comprar_agora', 'proximos_30_dias', 'pesquisando']),
  rendaFaixa: z.enum(['ate_3k', '3k_6k', '6k_10k', 'acima_10k']).optional(),
  temTroca: z.boolean().default(false),
  nome: z.string().trim().min(2).max(120),
  telefone,
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email()
    .max(254)
    .optional()
    .or(z.literal('').transform(() => undefined)),
  consentimento: z.literal(true, {
    error: 'É preciso concordar com o uso dos dados para contato',
  }),
  ...antiRobo,
});
export type SimulacaoPublica = z.input<typeof simulacaoPublicaSchema>;

export const reservaPublicaSchema = z.object({
  veiculoId: z.string().uuid(),
  nome: z.string().trim().min(2).max(120),
  telefone,
  email: z.string().trim().toLowerCase().email().max(254),
  consentimento: z.literal(true, {
    error: 'É preciso concordar com o uso dos dados',
  }),
  ...antiRobo,
});
export type ReservaPublica = z.input<typeof reservaPublicaSchema>;

export const eventoSiteSchema = z.object({
  tipo: z.enum(['visita', 'ver_carro', 'clique_whatsapp', 'ver_simulador']),
  veiculoId: z.string().uuid().optional(),
});

export const filtroVitrineSchema = z.object({
  marca: z.string().max(60).optional(),
  modelo: z.string().max(80).optional(),
  busca: z.string().max(80).optional(),
  precoMin: z.coerce.number().int().min(0).optional(),
  precoMax: z.coerce.number().int().min(0).optional(),
  anoMin: z.coerce.number().int().min(1950).optional(),
  kmMax: z.coerce.number().int().min(0).optional(),
  cambio: z.string().max(40).optional(),
  combustivel: z.string().max(40).optional(),
  filial: z.string().uuid().optional(),
  situacao: z.enum(['a_venda', 'preparacao']).default('a_venda'),
  ordem: z.enum(['recentes', 'menor_preco', 'maior_preco', 'menor_km']).default('recentes'),
  pagina: z.coerce.number().int().min(1).max(500).default(1),
  porPagina: z.coerce.number().int().min(1).max(48).default(24),
});
export type FiltroVitrine = z.infer<typeof filtroVitrineSchema>;

// ───────────── Respostas públicas (somente campos que podem aparecer no site) ─────────────

export interface VitrineFilial {
  id: string;
  nome: string;
  endereco: string | null;
  telefone: string | null;
  whatsapp: string | null;
}

export interface VitrineLoja {
  nome: string;
  slug: string;
  corPrimaria: string | null;
  logoUrl: string | null;
  premium: boolean;
  tema: TemaSite | null;
  titulo: string;
  descricao: string | null;
  cidade: string | null;
  estado: string | null;
  bannerTitulo: string | null;
  bannerTexto: string | null;
  filiais: VitrineFilial[];
  /** quando o site é de uma filial só */
  filialDoSite: string | null;
  marcas: string[];
  simulador: SimuladorConfig;
  reservaOnline: { sinalCentavos: number } | null;
  campanhas: { slug: string; titulo: string }[];
  /** Chat do site ligado (Central de Atendimento no plano) */
  chat: boolean;
}

export interface VitrineVeiculo {
  id: string;
  slug: string;
  marca: string;
  modelo: string;
  versao: string | null;
  anoFabricacao: number;
  anoModelo: number;
  km: number;
  cor: string | null;
  combustivel: string | null;
  cambio: string | null;
  status: 'disponivel' | 'reservado' | 'preparacao';
  etapa: EtapaPreparacao | null;
  precoCentavos: number | null;
  fipeCentavos: number | null;
  foto: { p: string; m: string } | null;
  filialId: string;
}

export interface VitrineVeiculoDetalhe extends VitrineVeiculo {
  opcionais: string[];
  descricao: string;
  fotos: { p: string; m: string; g: string }[];
  linhaDoTempo: { quando: string; tipo: string; descricao: string }[];
  filial: VitrineFilial | null;
  atualizadoEm: string;
}

export interface VitrineLista {
  itens: VitrineVeiculo[];
  total: number;
  pagina: number;
  paginas: number;
}

/** Campos que NUNCA podem aparecer em resposta pública (teste automatizado). */
export const CAMPOS_PROIBIDOS_VITRINE = [
  'valorCompraCentavos',
  'valor_compra_centavos',
  'custo',
  'custos',
  'custoTotalCentavos',
  'custo_total_centavos',
  'margem',
  'margemPrevistaCentavos',
  'lucro',
  'lucroRealCentavos',
  'placa',
  'chassi',
  'renavam',
  'observacoes',
  'fornecedor',
  'valorCentavos',
  'cpf',
  'telefoneCliente',
  'email',
  'tenantId',
  'tenant_id',
  'chaveOriginal',
  'chave_original',
] as const;

export const chatIniciarSchema = z.object({
  nome: z.string().trim().min(2, 'Informe seu nome').max(120),
  telefone,
  texto: z.string().trim().min(1, 'Escreva sua mensagem').max(2000),
  veiculoId: z.string().uuid().optional(),
  consentimento: z.literal(true, {
    error: 'Autorize o contato para continuar',
  }),
  ...antiRobo,
});
export const chatMensagemSchema = z.object({ texto: z.string().trim().min(1).max(2000) });

// ───────────── Site próprio da loja (API externa com chave pública) ─────────────

/**
 * Endereço do site da loja que pode chamar a API pelo navegador (CORS).
 * Só https (ou http://localhost para testes), sem caminho: guarda a origem normalizada.
 */
export const origemSiteSchema = z
  .string()
  .trim()
  .max(200)
  .transform((t, c) => {
    try {
      const u = new URL(t);
      const local = u.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(u.hostname);
      if (u.protocol !== 'https:' && !local) throw new Error();
      if (u.username || u.password) throw new Error();
      return u.origin.toLowerCase();
    } catch {
      c.addIssue({
        code: 'custom',
        message: 'Use o endereço completo, ex.: https://www.sualoja.com.br',
      });
      return z.NEVER;
    }
  });

export const origensSiteSchema = z.object({
  origens: z.array(origemSiteSchema).max(10, 'No máximo 10 endereços'),
});
export type OrigensSite = z.input<typeof origensSiteSchema>;

export type ChaveSiteResumo =
  { ativa: false } | { ativa: true; prefixo: string; origens: string[]; criadaEm: string };
